import React from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Card, Title, Group, Button, Text, Badge, Alert } from '@mantine/core';
import {
  IconPlus,
  IconDeviceFloppy,
  IconCheck,
  IconAlertTriangle,
  IconSend,
  IconArrowBackUp,
  IconInfoCircle,
} from '@tabler/icons-react';
import { EssayContent, EssaySection, EssayStatus, ESSAY_SECTIONS } from '../../hooks/useEssayApi';
import { EssayGuide } from './EssayGuide';
import { EssayPreview } from './EssayPreview';
import { EssaySortableList } from './EssaySortableList';
import { newNode, setText, addChild, removeNode, insertAfter, moveNode } from './essayTree';
import { useEssayAutosave, SaveState } from './useEssayAutosave';
import { ReturnForReworkButton, ReturnedNotice } from './ReturnForRework';
import { ReworkNote } from '../../hooks/useEssayApi';
import { EssayDocxImportButton } from './EssayDocxImportButton';

interface Props {
  essayId: string;
  initialContent: EssayContent;
  readOnly?: boolean;
  status?: EssayStatus;
  /** Показывать ли кнопки смены статуса (владелец / ревьюер / админ). */
  canSubmit?: boolean;
  /** Эссе заведено импортом — автор должен видеть, что текст писал не он. */
  importedAt?: string | null;
  /** Версия эссе с сервера — для защиты от затирания чужой правки. */
  updatedAt?: string;
  onSubmit?: () => void;
  onUnsubmit?: () => void;
  isStatusPending?: boolean;
  /** Пометка «вернули на доработку», если она стоит. */
  returnedForRework?: ReworkNote | null;
  /** Может ли смотрящий вернуть эссе автору. */
  canReturn?: boolean;
  onReturn?: (comment: string) => Promise<unknown>;
  isReturnPending?: boolean;
}

const FLAT_SECTIONS = ESSAY_SECTIONS.filter((s) => s !== 'done');

function SaveBadge({ state, t }: { state: SaveState; t: (k: string) => string }) {
  if (state === 'saving')
    return (
      <Badge color="gray" variant="light">
        {t('essays.saving')}
      </Badge>
    );
  if (state === 'saved')
    return (
      <Badge color="teal" variant="light" leftSection={<IconCheck size={12} />}>
        {t('essays.saved')}
      </Badge>
    );
  if (state === 'error')
    return (
      <Badge color="red" variant="light" leftSection={<IconAlertTriangle size={12} />}>
        {t('essays.saveError')}
      </Badge>
    );
  return null;
}

export const EssayEditor: React.FC<Props> = ({
  essayId,
  initialContent,
  readOnly,
  status,
  canSubmit,
  importedAt,
  updatedAt,
  onSubmit,
  onUnsubmit,
  isStatusPending,
  returnedForRework,
  canReturn,
  onReturn,
  isReturnPending,
}) => {
  const { t } = useTranslation();
  const { content, update, saveNow, saveState, saveErrorMessage, isConflict, reload } =
    useEssayAutosave(essayId, initialContent, readOnly, importedAt, updatedAt);
  const isSubmitted = status === 'submitted';
  const [focusId, setFocusId] = React.useState<string | null>(null);

  // Сначала дожимаем автосейв: отложенный на 800 мс PUT ушёл бы уже после
  // смены статуса и получил бы 403.
  const handleSubmit = async () => {
    if (await saveNow()) onSubmit?.();
  };

  const onChangeText = (section: EssaySection) => (id: string, text: string) =>
    update((c) => ({ ...c, [section]: setText(c[section], id, text) }));
  const onAddChild = (section: EssaySection) => (id: string) =>
    update((c) => ({ ...c, [section]: addChild(c[section], id) }));
  const onRemove = (section: EssaySection) => (id: string) =>
    update((c) => ({ ...c, [section]: removeNode(c[section], id) }));
  const onAddTop = (section: EssaySection) => () => {
    const node = newNode();
    update((c) => ({ ...c, [section]: [...c[section], node] }));
    setFocusId(node.id);
  };
  const onMove =
    (section: EssaySection) => (activeId: string, parentId: string | null, index: number) =>
      update((c) => ({ ...c, [section]: moveNode(c[section], activeId, parentId, index) }));
  const onAddSibling = (section: EssaySection) => (id: string) => {
    const node = newNode();
    update((c) => ({ ...c, [section]: insertAfter(c[section], id, node) }));
    setFocusId(node.id);
  };

  return (
    <Stack gap="md">
      {!readOnly && <EssayGuide />}

      <Group justify="flex-end" gap="sm">
        {!readOnly && <SaveBadge state={saveState} t={t} />}
        {!readOnly && (
          <EssayDocxImportButton content={content} onImport={(parsed) => update(() => parsed)} />
        )}
        {!readOnly && (
          <Button
            variant="default"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => void saveNow()}
            loading={saveState === 'saving'}
          >
            {t('common.save')}
          </Button>
        )}
        {canReturn && onReturn && isSubmitted && (
          <ReturnForReworkButton onReturn={onReturn} isPending={isReturnPending} />
        )}
        {canSubmit &&
          (isSubmitted ? (
            <Button
              variant="light"
              leftSection={<IconArrowBackUp size={16} />}
              onClick={() => onUnsubmit?.()}
              loading={isStatusPending}
            >
              {t('essays.unsubmit')}
            </Button>
          ) : (
            <Button
              leftSection={<IconSend size={16} />}
              onClick={() => void handleSubmit()}
              loading={isStatusPending || saveState === 'saving'}
            >
              {t('essays.submit')}
            </Button>
          ))}
      </Group>

      {saveErrorMessage && (
        <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
          <Stack gap="xs" align="flex-start">
            <Text size="sm">{saveErrorMessage}</Text>
            {isConflict && (
              <Button size="xs" variant="light" color="red" onClick={reload}>
                {t('essays.reloadEssay')}
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      {/* Без пометки импортированное эссе неотличимо от написанного самим
          человеком — а спрашивать по нему будут именно с него. */}
      {importedAt && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          {t('essays.importedNotice')}
        </Alert>
      )}

      <ReturnedNotice note={returnedForRework} />

      {isSubmitted && readOnly && (
        <Alert color="teal" variant="light" icon={<IconInfoCircle size={16} />}>
          {t('essays.submittedNotice')}
        </Alert>
      )}

      {/* Читать эссе удобнее так же, как оно выглядит в отчёте, а не полями ввода. */}
      {readOnly ? (
        <EssayPreview content={content} />
      ) : (
        <>
          {/* Done — recursive tree; the flat sections reuse the same node in flat mode. */}
          <Card withBorder radius="md" padding="md">
            <Title order={4} mb="sm">
              {t('essays.sections.done')}
            </Title>
            {content.done.length === 0 && (
              <Text c="dimmed" size="sm" mb="sm">
                {t('essays.emptySection')}
              </Text>
            )}
            <EssaySortableList
              nodes={content.done}
              onChangeText={onChangeText('done')}
              onAddChild={onAddChild('done')}
              onRemove={onRemove('done')}
              onAddSibling={onAddSibling('done')}
              onMove={onMove('done')}
              focusId={focusId}
            />
            <Button
              variant="light"
              color="teal"
              leftSection={<IconPlus size={16} />}
              mt="sm"
              onClick={onAddTop('done')}
            >
              {t('essays.addGroup')}
            </Button>
          </Card>

          {FLAT_SECTIONS.map((section) => (
            <Card key={section} withBorder radius="md" padding="md">
              <Title order={4} mb="sm">
                {t(`essays.sections.${section}`)}
              </Title>
              {content[section].length === 0 && (
                <Text c="dimmed" size="sm" mb="sm">
                  {t('essays.emptySection')}
                </Text>
              )}
              <EssaySortableList
                nodes={content[section]}
                allowChildren={false}
                onChangeText={onChangeText(section)}
                onAddChild={() => {}}
                onRemove={onRemove(section)}
                onAddSibling={onAddSibling(section)}
                onMove={onMove(section)}
                focusId={focusId}
              />
              <Button
                variant="light"
                color="teal"
                leftSection={<IconPlus size={16} />}
                mt="sm"
                onClick={onAddTop(section)}
              >
                {t('essays.addItem')}
              </Button>
            </Card>
          ))}
        </>
      )}
    </Stack>
  );
};
